import type { InvoiceListItem } from '@repo/shared-types';

/**
 * The status-filter model behind the invoices list — moved here verbatim from
 * `InvoicesPage.tsx` (P2.2) so the count chips and the rows they filter run the
 * SAME predicate rather than two copies that can drift.
 *
 * Values match the stored InvoiceStatus enum values, kept as plain strings:
 * @repo/shared-types re-exports its enums as type-only and the rest of the FE
 * already treats `invoice.status` as a string (see InvoiceStatusBadge,
 * bulkInvoiceSelection.ts). SENT has no dedicated filter — it still shows up
 * under "Tất cả". OVERDUE is derived (`Invoice.isOverdue`), never stored.
 */
export const STATUS_FILTER_OPTIONS = ['DRAFT', 'PENDING', 'OVERDUE', 'PARTIAL', 'PAID', 'CANCELLED'] as const;

export type StatusFilterOption = (typeof STATUS_FILTER_OPTIONS)[number];
export type StatusFilter = 'ALL' | StatusFilterOption;

/** The two fields every count/filter decision reads — so callers can pass rows
 *  straight out of the invoices list query untouched. */
export type CountableInvoice = Pick<InvoiceListItem, 'status' | 'isOverdue'>;

/**
 * Mirrors the isOverdue-wins-over-stored-status rule `InvoiceStatusBadge`
 * renders: an overdue PENDING/PARTIAL invoice belongs under OVERDUE and
 * nowhere else.
 */
export function matchesStatusFilter(invoice: CountableInvoice, filter: StatusFilter): boolean {
  if (filter === 'ALL') return true;
  if (filter === 'OVERDUE') return invoice.isOverdue;
  // An overdue PENDING/PARTIAL invoice belongs under OVERDUE, not here.
  if (filter === 'PENDING' || filter === 'PARTIAL') {
    return invoice.status === filter && !invoice.isOverdue;
  }
  return invoice.status === filter;
}

export interface InvoiceCounts {
  total: number;
  draft: number;
  pending: number;
  overdue: number;
  partial: number;
  paid: number;
  cancelled: number;
}

/** Which `InvoiceCounts` bucket each filter fills. One table, so a chip's
 *  label, its number and its filter can never be wired to three different
 *  things. */
export const COUNT_KEY_BY_FILTER: Record<StatusFilterOption, keyof Omit<InvoiceCounts, 'total'>> = {
  DRAFT: 'draft',
  PENDING: 'pending',
  OVERDUE: 'overdue',
  PARTIAL: 'partial',
  PAID: 'paid',
  CANCELLED: 'cancelled',
};

/**
 * Window-scoped counts behind the live subtitle and the filter chips (design
 * §1/§2). Pass the SAME array the list renders before status/search filtering
 * — i.e. `invoices`, already scoped to the month window by the query — not
 * `visibleInvoices`, or the chips would count only what the active chip
 * already shows.
 *
 * Every bucket is derived by running `matchesStatusFilter`, which is the whole
 * point: chip number === row count, by construction.
 *
 * The buckets deliberately do NOT sum to `total`. An overdue PENDING invoice
 * is counted under `overdue` only (the OVERDUE-wins rule), and a SENT invoice
 * is counted in no bucket at all (it has no filter). `total` is the row count
 * of the window, nothing more.
 */
export function invoiceCounts(invoices: CountableInvoice[]): InvoiceCounts {
  const counts: InvoiceCounts = {
    total: invoices.length,
    draft: 0,
    pending: 0,
    overdue: 0,
    partial: 0,
    paid: 0,
    cancelled: 0,
  };
  for (const invoice of invoices) {
    for (const filter of STATUS_FILTER_OPTIONS) {
      if (matchesStatusFilter(invoice, filter)) counts[COUNT_KEY_BY_FILTER[filter]] += 1;
    }
  }
  return counts;
}
